import React, { useCallback, useState } from 'react';

// UI
import { GenericFieldWithErrorsProps } from './GenericFieldWithErrors';
import { makeStyles, createStyles, Theme } from '@material-ui/core';
import { DatePickerFieldUI } from '@cube3/ui/src/datepicker/DatePickerFieldUI';

import { makeDateString } from '@cube3/common/utils/makeDateString';
import { Typography } from '@cube3/cubicle/src/core/atoms/Typography';
import { VBox } from '@cube3/cubicle/src/core/templates/layout/Flex';
import { shouldShowErrors } from '../utils/shouldShowErrors';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    inputComponent: {
      padding: '0px',
      height: '40px',
      background: 'rgba(0, 0, 0, 0.16)',
      ...theme.typographyStyles.body1 // spread the 'default' text body properties.
    },
    notSet: {
      color: 'rgba(255, 255, 255, 0.2)'
    },
    error: {
      marginTop: 10
    },
    textField: {
      width: 210
    }
  })
);

export type DatePickerProps = {
  dateFormat?: string;
  initialFocusedDate: object | string | number | Date | null | undefined;
  inputVariant?: 'filled' | 'standard' | 'outlined';
  autoOk?: boolean;
  disablePast?: boolean;
  minDate?: object | string | number | Date | null | undefined;
  fullWidth?: boolean;
  showClearButton?: boolean;
  yearOnly: boolean;
};

interface DateFieldComponentProps
  extends Partial<GenericFieldWithErrorsProps>, DatePickerProps {}

function DatePicker(props: DateFieldComponentProps) {
  const classes = useStyles(props);

  const {
    //input
    onChange,
    value,
    onBlur,
    //meta
    warning,
    touched,
    active,
    error,
    //
    yearOnly,
    showClearButton = true,
    required,
    initialFocusedDate,
    showErrors = 'touched',
    dateFormat = 'D MMM YYYY',
    ...restProps
  } = props;

  const [internalValue, setIntervalValue] = useState(value || undefined);

  const handleChange = useCallback((moment) => {
    const val = moment.startOf('day').toDate();
    onChange(val);
    setIntervalValue(val);
  }, []);

  const handleClear = useCallback(() => {
    onChange(undefined); // set the value to '' will get a 'invalid date format' error
    setIntervalValue(undefined);
  }, []);

  const shouldShowClear = showClearButton && !required && internalValue;

  return (
    <VBox gap={4}>
      {yearOnly ? (
        <DatePickerFieldUI
          {...restProps}
          InputProps={{
            classes: {
              root: `${classes.inputComponent} ${
                !internalValue && classes.notSet
              }`
            },
            ...{ 'data-cy': props['data-cy'] }
          }}
          value={value}
          onChange={handleChange}
          views={['year']}
          format={'YYYY'}
          showClearButton={shouldShowClear}
          handleClear={handleClear}
          warning={warning || restProps['warning']}
          onBlur={onBlur}
          initialFocusedDate={initialFocusedDate}
        />
      ) : (
        <DatePickerFieldUI
          labelFunc={(date) =>
            makeDateString({
              date: date.toISOString(),
              noTime: true,
              fullForm: true
            }).dateString
          }
          InputProps={{
            classes: {
              root: `${classes.inputComponent} ${
                !internalValue && classes.notSet
              }`
            },
            ...{ 'data-cy': props['data-cy'] }
          }}
          initialFocusedDate={initialFocusedDate}
          value={internalValue}
          onChange={handleChange}
          handleClear={handleClear}
          showClearButton={shouldShowClear}
          data-cy={props['data-cy']}
          {...restProps}
          warning={warning || restProps['warning']}
          format={dateFormat}
          onBlur={onBlur}
        />
      )}
      {shouldShowErrors(showErrors, touched, active) && error && (
        <div className={classes.error}>
          <Typography color="critical" colorVariant="02" weight="body">
            {error}
          </Typography>
        </div>
      )}
    </VBox>
  );
}

export default DatePicker;
